import { useMutation, useQuery } from '@tanstack/react-query';
import secureLocalStorage from 'react-secure-storage';

import { serverRequest, serverRequest2 } from "../util/server_request";
import { extractErrorMessage } from './extractErrorMessage';
import { notifyUser } from '../util/util';

const notifiedErrors = new Set();

// Helper function to notify only once for the same error
const notifyOnce = (type, message, title = "") => {
  if (!notifiedErrors.has(message)) {
    notifiedErrors.add(message);
    notifyUser(type, message, title);
  }
};

// Initiate pension payment
export function useInitiatePensionPayment() {
  return useMutation({
    mutationFn: async (paymentData) => {
      try {
        const formData = new FormData();
        formData.append('amount', paymentData.amount);
        formData.append('currency', paymentData.currency || '');

        const result = await serverRequest2('POST', 'initiate/pensions-payment', formData);
        if (!result || !result.success) {
          throw new Error(result?.message || 'Failed to initiate pension payment');
        }
        return result;
      } catch (error) {
        const errorMessage = extractErrorMessage(error, 'Failed to initiate pension payment');
        notifyOnce("error", errorMessage, "Payment Error");
        throw new Error(errorMessage);
      }
    },
  });
}

// Initiate life insurance payment
export function useInitiateLifePayment() {
  return useMutation({
    mutationFn: async (paymentData) => {
      try {
        const formData = new FormData();
        formData.append('amount', paymentData.amount);
        formData.append('currency', paymentData.currency || '');
        formData.append('policy_number', paymentData.policyNumber);
        formData.append('product', paymentData.product);

        const result = await serverRequest2('POST', 'initiate/life-insurance-payment', formData);
        if (!result || !result.success) {
          throw new Error(result?.message || 'Failed to initiate life insurance payment');
        }
        return result;
      } catch (error) {
        const errorMessage = extractErrorMessage(error, 'Failed to initiate life insurance payment');
        notifyOnce("error", errorMessage, "Payment Error");
        throw new Error(errorMessage);
      }
    },
  });
}

// Get pension payment history
export function usePensionPaymentHistory(filters = {}) {
  const { status = '', amount = '', payment_reference = '', client_reference = '' } = filters;

  return useQuery({
    queryKey: ['pension-payment-history', filters],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (status) params.append('filter[status]', status);
        if (amount) params.append('filter[amount]', amount);
        if (payment_reference) params.append('filter[payment_reference]', payment_reference);
        if (client_reference) params.append('filter[client_reference]', client_reference);

        const queryString = params.toString();
        const endpoint = queryString ? `payment/pensions-payments?${queryString}` : 'payment/pensions-payments';

        const result = await serverRequest('GET', endpoint);
        if (!result || !result.success) {
          throw new Error(result?.message || 'Failed to fetch pension payment history');
        }
        return result;
      } catch (error) {
        const errorMessage = extractErrorMessage(error, 'Failed to fetch pension payment history');
        notifyOnce("error", errorMessage, "Error");
        throw new Error(errorMessage);
      }
    },
    enabled: !!secureLocalStorage.getItem('token'),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// Get life insurance payment history
export function useLifePaymentHistory(filters = {}) {
  const { policy_number = '', status = '', amount = '', payment_reference = '', client_reference = '' } = filters;

  return useQuery({
    queryKey: ['life-payment-history', filters],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (policy_number) params.append('filter[policy_number]', policy_number);
        if (status) params.append('filter[status]', status);
        if (amount !== undefined && amount !== null) params.append('filter[amount]', amount);
        if (payment_reference !== undefined && payment_reference !== null) params.append('filter[payment_reference]', payment_reference);
        if (client_reference !== undefined && client_reference !== null) params.append('filter[client_reference]', client_reference);

        const queryString = params.toString();
        const endpoint = queryString ? `payment/life-payments?${queryString}` : 'payment/life-payments';

        const result = await serverRequest('GET', endpoint);
        if (!result || !result.success) {
          throw new Error(result?.message || 'Failed to fetch life payment history');
        }
        return result;
      } catch (error) {
        const errorMessage = extractErrorMessage(error, 'Failed to fetch life payment history');
        notifyOnce("error", errorMessage, "Error");
        throw new Error(errorMessage);
      }
    },
    enabled: !!secureLocalStorage.getItem('token'),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// Get telco payment methods
export function useTelcoPaymentMethods() {
  return useQuery({
    queryKey: ['telco-payment-methods'],
    queryFn: async () => {
      try {
        const result = await serverRequest('GET', 'policy/telco/pay-methods');
        if (!result || !result.success) {
          throw new Error(result?.message || 'Failed to fetch telco payment methods');
        }
        return result;
      } catch (error) {
        const errorMessage = extractErrorMessage(error, 'Failed to fetch telco payment methods');
        notifyOnce("error", errorMessage, "Error");
        throw new Error(errorMessage);
      }
    },
    enabled: !!secureLocalStorage.getItem('token'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Submit claim request
export function useClaimRequest() {
  return useMutation({
    mutationFn: async (claimData) => {
      try {
        const formData = new FormData();
        formData.append('policy_number', claimData.policyNumber);
        formData.append('CurrentCashValue', claimData.currentCashValue);
        formData.append('claim_amount', claimData.claimAmount);
        formData.append('claim_default_telcomethod', claimData.telecomMethod);
        formData.append('claim_default_momo_wallet', claimData.momoWallet);

        const result = await serverRequest2('POST', 'policy/claim-request', formData);
        if (!result || !result.success) {
          // Handle message as object or string
          let message = result?.message || 'Failed to submit claim request';
          if (typeof message === 'object') {
            // Convert validation errors object to string
            const errors = [];
            Object.keys(message).forEach(key => {
              // Replace underscores with spaces for better readability
              const fieldLabel = key.replace(/_/g, ' ');
              if (Array.isArray(message[key])) {
                errors.push(`${fieldLabel}: ${message[key].join(', ')}`);
              } else {
                errors.push(`${fieldLabel}: ${message[key]}`);
              }
            });
            message = errors.join('. ') || 'Validation failed';
          }
          throw new Error(message);
        }
        return result;
      } catch (error) {
        const errorMessage = extractErrorMessage(error, 'Failed to submit claim request');
        notifyOnce("error", errorMessage, "Claim Error");
        throw new Error(errorMessage);
      }
    },
  });
}
